/**
 * ReferenceDocumentsSection — Reference > Documents (docs/REFERENCE_DOCUMENTS.md).
 *
 * The files we RECEIVE for a project (surveys, existing drawings, photos, reports, consultant sets,
 * city approvals), kept once per project. Grouped by the kit item each file answers, then "Other
 * files". Separate from a milestone's Documents list, which holds what we ISSUE.
 *
 * A Reference child root (the MeetingsSection mould): indented under Reference when expanded,
 * un-indented when Reference is zoomed, children-only when zoomed itself (wf.zoomRoot ===
 * 'documents'), and a group zoom 'refdoc-grp:<factId|none>'.
 *
 * Cache: ctx.refDocCache[projectId] — undefined not started, null in flight, { documents, facts }.
 * Reloads on 'reference-documents-changed'.
 */

import { useEffect, useReducer } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { confirmDialog, promptDialog, alertDialog } from './Dialog.jsx'
import {
  listReferenceDocuments, openReferenceDocument, updateReferenceDocument, deleteReferenceDocument,
  announceReferenceDocumentsChanged,
} from '../services/referenceDocuments.js'
import { fmtSize } from './ReferenceDocumentModal.jsx'

function render() { window.dispatchEvent(new Event('outline-render')) }

/** Loads (once) the project's documents into ctx.refDocCache. Exported for the Parcel view. */
export function loadReferenceDocuments(projectId, { force = false } = {}) {
  if (!projectId) return
  if (!ctx.refDocCache) ctx.refDocCache = {}
  if (!force && ctx.refDocCache[projectId] !== undefined) return
  ctx.refDocCache[projectId] = null
  listReferenceDocuments(projectId)
    .then(r => { ctx.refDocCache[projectId] = { documents: r.documents || [], facts: r.facts || [], factStatus: r.factStatus || {} }; render() })
    .catch(() => { ctx.refDocCache[projectId] = { documents: [], facts: [], error: true }; render() })
}

/**
 * Would taking this file away from its kit item reopen that item? True when the item is ticked
 * "in hand" on this project and this is its last file (the server does the reopening; this only
 * decides whether to warn). docs/REFERENCE_DOCUMENTS.md "Reopening".
 */
export function reopenWarning(projectId, doc) {
  const c = ctx.refDocCache && ctx.refDocCache[projectId]
  if (!doc || !doc.fact_id || !c) return null
  if ((c.factStatus || {})[doc.fact_id] !== 'complete') return null
  const others = (c.documents || []).filter(d => d.fact_id === doc.fact_id && d.id !== doc.id).length
  if (others > 0) return null
  return `"${doc.fact_name || 'This kit item'}" is marked in hand, and this is its only file. Doing this puts the item back to open on every milestone that needs it.`
}

/** Tells the person which kit item reopened, after the server did it. */
export function reportReopened(name) {
  if (!name) return
  alertDialog({ title: 'Kit item reopened', message: `"${name}" has no file now, so it is open again on every milestone that needs it.` })
}

/** Groups documents by kit item, items in name order, untied files last. */
export function groupReferenceDocuments(documents) {
  const map = new Map()
  for (const d of documents || []) {
    const k = d.fact_id || 'none'
    if (!map.has(k)) map.set(k, { id: k, label: d.fact_id ? (d.fact_name || 'Kit item') : 'Other files', docs: [] })
    map.get(k).docs.push(d)
  }
  return [...map.values()].sort((a, b) => (a.id === 'none') - (b.id === 'none') || a.label.localeCompare(b.label))
}

function shortLabel(s) { return s && s.length > 80 ? s.slice(0, 78) + '…' : s }

function calcVis() {
  if (!wf.zoomProjectId) return 'hidden'
  const clean = !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery
  if (!clean) return 'hidden'
  if (wf.zoomSection && wf.zoomSection.indexOf('refdoc-grp:') === 0) return 'groupZoom'
  if (wf.zoomSection) return 'hidden'
  if (wf.zoomRoot === 'documents') return 'rootZoom'
  if (wf.zoomRoot === 'reference') return 'referenceZoom'
  if (!wf.zoomRoot) return 'home'
  return 'hidden'
}

function openUpload(projectId, factId) {
  window.dispatchEvent(new CustomEvent('open-reference-doc-modal', { detail: { projectId, factId: factId && factId !== 'none' ? factId : null } }))
}

export default function ReferenceDocumentsSection() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  useEffect(() => {
    function onRender() { forceUpdate() }
    function onChanged(e) { const p = e.detail && e.detail.projectId; if (p) loadReferenceDocuments(p, { force: true }) }
    window.addEventListener('outline-render', onRender)
    window.addEventListener('reference-documents-changed', onChanged)
    return () => { window.removeEventListener('outline-render', onRender); window.removeEventListener('reference-documents-changed', onChanged) }
  }, [])

  const vis = calcVis()
  const projectId = wf.zoomProjectId
  const rootCollapsed = wf.collapsed['root:documents'] !== false
  const showing = vis === 'rootZoom' || vis === 'groupZoom' ||
    ((vis === 'home' || vis === 'referenceZoom') && !rootCollapsed && (vis === 'referenceZoom' || wf.collapsed['root:reference'] === false))

  // Lazy load: fire whenever the list is on screen and its cache is empty — covers a page refresh with
  // it left open, and a Kit tick elsewhere dropping the cache. loadReferenceDocuments marks the entry
  // in flight (null) before fetching, so this cannot loop.
  useEffect(() => {
    if (showing && projectId && (!ctx.refDocCache || ctx.refDocCache[projectId] === undefined)) loadReferenceDocuments(projectId)
  })

  const menu = useActionMenu()
  if (vis === 'hidden') return null
  if (vis === 'home' && wf.collapsed['root:reference'] !== false) return null

  const cache = ctx.refDocCache ? ctx.refDocCache[projectId] : undefined
  const docs = cache && cache.documents
  const groups = docs ? groupReferenceDocuments(docs) : []

  const body = !cache
    ? <Filler text="Loading…" />
    : cache.error ? <Filler text="Could not load documents." />
    : groups.length === 0 ? <Filler text="No documents yet. Use ⋯ → Upload documents." />
    : groups.map(g => <GroupNode key={g.id} group={g} projectId={projectId} />)

  if (vis === 'rootZoom') return <div className="wf-list">{body}</div>
  if (vis === 'groupZoom') {
    const id = wf.zoomSection.slice('refdoc-grp:'.length)
    const g = groups.find(x => x.id === id)
    return <div className="wf-list">{!cache ? <Filler text="Loading…" /> : g ? g.docs.map(d => <DocRow key={d.id} doc={d} projectId={projectId} />) : <Filler text="No files here any more." />}</div>
  }

  const rootNode = (
    <div className={`wf-node wf-node--root wf-node--root-documents${rootCollapsed ? ' wf-node--collapsed' : ''}`} data-key="root:documents">
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key="root:documents">
          <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--root" title="Focus on Documents" data-fn="zoomTo" data-level="root" data-rootname="documents"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--root">Documents</span>
          {docs && <span className="wf-prog" style={{ marginLeft: 8, color: '#7a9aaa' }}>{docs.length}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); openUpload(projectId) }}>Upload documents</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => { menu.close(); ctx.expandCollapseDescendants('root:documents', true) }}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); ctx.expandCollapseDescendants('root:documents', false) }}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!rootCollapsed && <div className="wf-children">{body}</div>}
    </div>
  )
  return <div className="wf-list">{vis === 'referenceZoom' ? rootNode : <div className="wf-ref-child">{rootNode}</div>}</div>
}

function Filler({ text }) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0.3 }}></span>
        <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>{text}</span></div>
      </div>
    </div>
  )
}

function GroupNode({ group, projectId }) {
  const key = 'refdoc-grp:' + group.id
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Focus on these files"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'documents' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section" title={group.label}>{shortLabel(group.label)}</span>
          <span className="wf-prog" style={{ marginLeft: 8, color: '#7a9aaa' }}>{group.docs.length}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); openUpload(projectId, group.id) }}>Upload more</ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children">{group.docs.map(d => <DocRow key={d.id} doc={d} projectId={projectId} />)}</div>}
    </div>
  )
}

function DocRow({ doc, projectId }) {
  const menu = useActionMenu()
  const open = () => openReferenceDocument(doc.id).catch(e => alertDialog({ title: 'Could not open it', message: String(e.message || e) }))
  async function rename() {
    const v = await promptDialog({ title: 'Rename', defaultValue: doc.name, required: true })
    if (v === null) return
    try { await updateReferenceDocument(doc.id, { name: v }); announceReferenceDocumentsChanged(projectId) }
    catch (e) { alertDialog({ title: 'That did not save', message: String(e.message || e) }) }
  }
  async function remove() {
    const warn = reopenWarning(projectId, doc)
    if (!(await confirmDialog({ title: 'Delete this file?', message: doc.name + (warn ? '\n\n' + warn : ''), confirmLabel: warn ? 'Delete and reopen' : 'Delete', danger: true }))) return
    try { const r = await deleteReferenceDocument(doc.id); announceReferenceDocumentsChanged(projectId); reportReopened(r && r.reopened) }
    catch (e) { alertDialog({ title: 'That did not delete', message: String(e.message || e) }) }
  }
  const when = doc.created_at ? new Date(doc.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''
  return (
    <div className="wf-node wf-node--item" data-key={'refdoc:' + doc.id}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ cursor: 'pointer' }} onClick={open} title="Open">{doc.name}</span>
          <span style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--aj-text-muted)' }}>
            <span style={{ fontFamily: 'DM Mono, monospace' }}>{[fmtSize(doc.size_bytes), when].filter(Boolean).join(' · ')}</span>
            {doc.uploaded_by_name ? ' · ' + doc.uploaded_by_name : ''}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); open() }}>Open</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); rename() }}>Rename…</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); window.dispatchEvent(new CustomEvent('open-reference-doc-modal', { detail: { projectId, docId: doc.id } })) }}>Which kit item it answers…</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem danger onClick={() => { menu.close(); remove() }}>Delete</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}
